import { useState, useRef } from "react";
import { Play, Pause, Send, Eye, Music, Lightbulb } from "lucide-react";

const norm = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

function isMatch(guess, answer) {
  const g = norm(guess);
  const a = norm(answer);
  if (!g || !a) return false;
  return (a.includes(g) && g.length >= 3) || g.includes(a);
}

function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function BlindTestPlayer({ game, onComplete }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [guess, setGuess] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState("");
  const start = useRef(Date.now());
  const MAX = 3;

  const media = (game.media_url || "").trim();
  const ytId = youtubeId(media);
  const startAt = game.start_seconds || 0;
  const directSrc = ytId ? null : media || game.audio_url;
  const hasSource = Boolean(ytId || directSrc);

  if (!hasSource) {
    return <p className="text-center text-xl font-patient text-gray-500 mt-10">Ce blind test n'a pas encore d'extrait audio.</p>;
  }

  const toggle = () => {
    if (ytId) {
      setPlaying((p) => !p);
      return;
    }
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      if (startAt && el.currentTime < startAt) el.currentTime = startAt;
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const finish = (score) => {
    const duration = Math.round((Date.now() - start.current) / 1000);
    setPlaying(false);
    setTimeout(() => onComplete({ score, accuracy: score, duration }), 1400);
  };

  const submit = () => {
    if (revealed || !guess.trim()) return;
    const good = isMatch(guess, game.answer_title) || isMatch(guess, game.answer_artist);
    const nextAttempt = attempts + 1;
    setAttempts(nextAttempt);
    if (good) {
      setRevealed(true);
      setFeedback("Bravo, c'est exact !");
      finish(Math.max(60, 100 - (nextAttempt - 1) * 20));
    } else if (nextAttempt >= MAX) {
      setRevealed(true);
      setFeedback("Ce n'était pas tout à fait ça.");
      finish(40);
    } else {
      setFeedback(`Essayez encore (${MAX - nextAttempt} essai(s) restant(s))`);
      setGuess("");
    }
  };

  const reveal = () => {
    if (revealed) return;
    setRevealed(true);
    setFeedback("Voici la réponse.");
    finish(40);
  };

  return (
    <div className="w-full max-w-xl mx-auto text-center" data-testid="blindtest-player">
      <span className="inline-block text-lg font-patient font-bold text-white bg-pink-500 px-5 py-1.5 rounded-full mb-6">
        Années {game.decade || "?"}
      </span>
      <p className="text-2xl font-patient font-bold text-gray-700 mb-8">Écoutez l'extrait et devinez le titre ou l'artiste</p>

      {/* Hidden YouTube audio player (blind: video masqué) */}
      {ytId && playing && (
        <div className="relative w-40 h-40 mx-auto mb-8 rounded-full overflow-hidden">
          <iframe
            title="extrait"
            data-testid="blindtest-youtube"
            className="absolute -inset-40 w-[400px] h-[400px] pointer-events-none"
            src={`https://www.youtube.com/embed/${ytId}?autoplay=1&start=${startAt}&controls=0&modestbranding=1&rel=0`}
            allow="autoplay"
            frameBorder="0"
          />
          <div className="absolute inset-0 bg-emerald-700 flex items-center justify-center">
            <Music className="w-16 h-16 text-white" />
          </div>
        </div>
      )}
      {!ytId && <audio ref={audioRef} src={directSrc} onEnded={() => setPlaying(false)} data-testid="blindtest-audio" />}

      {!(ytId && playing) && (
        <button
          onClick={toggle}
          data-testid="blindtest-play-button"
          className={`w-28 h-28 mx-auto rounded-full bg-emerald-700 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform duration-150 mb-8 ${playing ? "" : "soft-pulse"}`}
        >
          {playing ? <Pause className="w-12 h-12" /> : <Play className="w-12 h-12 ml-1" />}
        </button>
      )}
      {ytId && playing && (
        <button onClick={toggle} data-testid="blindtest-play-button"
          className="w-28 h-28 mx-auto rounded-full bg-emerald-800 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform duration-150 mb-8">
          <Pause className="w-12 h-12" />
        </button>
      )}

      {game.hint && !revealed && (
        <div className="mb-6">
          {showHint ? (
            <div className="inline-flex items-start gap-2 rounded-2xl bg-amber-50 border-2 border-amber-200 px-5 py-3 text-amber-800 font-patient text-lg" data-testid="blindtest-hint">
              <Lightbulb className="w-6 h-6 shrink-0" /> <span>{game.hint}</span>
            </div>
          ) : (
            <button onClick={() => setShowHint(true)} data-testid="blindtest-hint-button"
              className="inline-flex items-center gap-2 h-14 px-6 rounded-2xl bg-amber-100 border-4 border-amber-200 text-amber-800 font-patient font-bold text-lg active:scale-95 transition-transform duration-150">
              <Lightbulb className="w-6 h-6" /> Indice
            </button>
          )}
        </div>
      )}

      {!revealed ? (
        <div className="space-y-4">
          <input
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Titre ou artiste…"
            data-testid="blindtest-guess-input"
            className="w-full h-16 rounded-2xl border-4 border-emerald-700/20 px-6 text-center font-patient text-2xl focus:outline-none focus:border-emerald-500"
          />
          {feedback && <p className="font-patient text-lg text-gray-500">{feedback}</p>}
          <div className="flex gap-3 justify-center">
            <button onClick={submit} data-testid="blindtest-submit-button"
              className="flex-1 h-16 rounded-2xl bg-emerald-700 text-white font-patient font-bold text-xl flex items-center justify-center gap-2 active:scale-95 transition-transform duration-150">
              <Send className="w-6 h-6" /> Valider
            </button>
            <button onClick={reveal} data-testid="blindtest-reveal-button"
              className="h-16 px-5 rounded-2xl bg-white border-4 border-emerald-700/20 text-gray-600 font-patient font-bold text-lg flex items-center gap-2 active:scale-95 transition-transform duration-150">
              <Eye className="w-6 h-6" /> Réponse
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl bg-emerald-50 border-4 border-emerald-600 p-6" data-testid="blindtest-answer">
          <p className="font-patient text-lg text-emerald-700 font-bold mb-1">{feedback}</p>
          <p className="font-patient text-3xl font-black text-gray-800">{game.answer_title || "—"}</p>
          {game.answer_artist && <p className="font-patient text-xl text-gray-600 mt-1">{game.answer_artist}</p>}
        </div>
      )}
    </div>
  );
}
